import { createPlugin } from "@backstage/core-plugin-api";
import {
    ApiBlueprint,
    createApiFactory,
    createFrontendPlugin,
    discoveryApiRef,
    identityApiRef,
} from "@backstage/frontend-plugin-api";
import { createCardExtension } from "@backstage/plugin-home-react";
import { PolicyCenterClient } from "@internal/backstage-plugin-policy-center-common";

import { policyCenterApiRef } from "./api";

/**
 * Legacy plugin registration — required for {@link PolicyCenterHomeCard},
 * which is built with the `createCardExtension` home-react API so it can
 * be placed in the `CustomHomepageGrid`.
 *
 * @deprecated This legacy plugin exists solely to support the home page
 * card component. All API registration is handled by the NFS default export.
 */
export const policyCenterPlugin = createPlugin({
    id: "policy-center",
});

export const PolicyCenterHomeCard = policyCenterPlugin.provide(
    createCardExtension<{ policyCenterUrl: string }>({
        name: "PolicyCenterHomeCard",
        title: "Policy Center Status",
        description: "Informs you of unacknowledged corporate policies.",
        components: () =>
            import(
                "./components/PolicyCenterHomeCard/PolicyCenterHomeCard"
            ).then(m => ({
                Content: m.Content,
            })),
    })
);

/**
 * API extension that registers the Policy Center backend client.
 *
 * Provides {@link PolicyCenterClient} backed by the Backstage discovery
 * and identity APIs.
 */
const policyCenterApiExt = ApiBlueprint.make({
    name: "policy-center",
    params: defineParams =>
        defineParams(
            createApiFactory({
                api: policyCenterApiRef,
                deps: {
                    discoveryApi: discoveryApiRef,
                    identityApi: identityApiRef,
                },
                factory: ({ discoveryApi, identityApi }) =>
                    new PolicyCenterClient({ discoveryApi, identityApi }),
            })
        ),
});

/**
 * The Policy Center frontend plugin.
 *
 * Auto-discovered via `app.packages: all` and registers the
 * {@link PolicyCenterClient} API factory so that policy-center components
 * can resolve `policyCenterApiRef` via `useApi`.
 *
 * @public
 */
const policyCenterFrontendPlugin = createFrontendPlugin({
    pluginId: "policy-center",
    extensions: [policyCenterApiExt],
});

export default policyCenterFrontendPlugin;
